// API Configuration
// Use explicit env var when provided (REACT_APP_API_URL for CRA),
// otherwise derive host from current page hostname and default backend port 3001.
// IMPORTANT: If page is loaded over HTTPS, use HTTPS for API to avoid Mixed Content errors
let apiHost = process.env.REACT_APP_API_URL;
if (!apiHost) {
  // If page is HTTPS, use HTTPS for API (important for Cloudflare Tunnel)
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  apiHost = `${protocol}//${window.location.hostname}:3001`;
}
// Avoid double '/api' if REACT_APP_API_URL already contains it
const normalizedHost = apiHost.replace(/\/+$/,'');
export const API_BASE_URL = normalizedHost.endsWith('/api') ? normalizedHost : `${normalizedHost}/api`;

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    PROFILE: '/auth/profile'
  },
  
  // Applications endpoints
  APPLICATIONS: '/applications',
  
  // References endpoints
  REFERENCES: '/references',
  CLIENTS: '/references/clients',
  WAREHOUSES: '/references/warehouses',
  EXPEDITORS: '/references/expeditors',
  
  // Routes endpoints
  ROUTES: '/routes',
  
  // Documents endpoints
  DOCUMENTS: '/documents',
  
  // Notifications endpoints
  NOTIFICATIONS: '/notifications',
  
  // Users endpoints
  USERS: '/users'
};

// Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'logistics_access_token',
  REFRESH_TOKEN: 'logistics_refresh_token',
  USER: 'logistics_user',
  THEME: 'logistics_theme'
};

// Application Status
export const APPLICATION_STATUSES = {
  CREATED: 'CREATED',
  IN_WORK: 'IN_WORK',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  OVERDUE: 'OVERDUE'
} as const;

/** Подписи статусов — совпадают с `ui/Status/StatusTag` (единый стиль). */
export const APPLICATION_STATUS_LABELS = {
  [APPLICATION_STATUSES.CREATED]: 'Создан',
  [APPLICATION_STATUSES.IN_WORK]: 'В работе',
  [APPLICATION_STATUSES.COMPLETED]: 'Завершён',
  [APPLICATION_STATUSES.CANCELLED]: 'Отменён',
  [APPLICATION_STATUSES.OVERDUE]: 'Просрочено'
};

export const APPLICATION_STATUS_COLORS: Record<string, string> = {
  [APPLICATION_STATUSES.CREATED]: 'blue',
  [APPLICATION_STATUSES.IN_WORK]: 'blue',
  [APPLICATION_STATUSES.COMPLETED]: 'green',
  [APPLICATION_STATUSES.CANCELLED]: 'default',
  [APPLICATION_STATUSES.OVERDUE]: 'red'
};

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  OPERATOR: 'operator',
  VIEWER: 'viewer'
} as const;

export const USER_ROLE_LABELS = {
  [USER_ROLES.ADMIN]: 'Администратор',
  [USER_ROLES.MANAGER]: 'Менеджер',
  [USER_ROLES.OPERATOR]: 'Оператор',
  [USER_ROLES.VIEWER]: 'Просмотр'
};

/** GAP-063: roles eligible as route/application responsible user */
export const RESPONSIBLE_USER_ROLE_NAMES = ['admin', 'logist'] as const;

/** GAP-063: compact parenthetical suffix for responsible-user labels */
export const RESPONSIBLE_ROLE_LABELS: Record<string, string> = {
  admin: 'админ',
  logist: 'логист',
};

// Route Status
export const ROUTE_STATUSES = {
  CREATED: 'created',
  IN_TRANSIT: 'in_transit',
  ISSUES: 'issues',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
} as const;

export const ROUTE_STATUS_LABELS = {
  [ROUTE_STATUSES.CREATED]: 'Создан',
  [ROUTE_STATUSES.IN_TRANSIT]: 'В пути',
  [ROUTE_STATUSES.ISSUES]: 'Проблемы',
  [ROUTE_STATUSES.COMPLETED]: 'Завершён',
  [ROUTE_STATUSES.CANCELLED]: 'Отменён'
};

// Cargo Status
export const CARGO_STATUSES = {
  IN_APPLICATION: 'in_application',
  IN_ROUTE: 'in_route', 
  ON_WAREHOUSE: 'on_warehouse',
  DELIVERED: 'delivered'
} as const;

export const CARGO_STATUS_LABELS = {
  [CARGO_STATUSES.IN_APPLICATION]: 'В заявке',
  [CARGO_STATUSES.IN_ROUTE]: 'В маршруте',
  [CARGO_STATUSES.ON_WAREHOUSE]: 'На складе (перевалке)',
  [CARGO_STATUSES.DELIVERED]: 'Доставлен'
};

export const CARGO_STATUS_COLORS = {
  [CARGO_STATUSES.IN_APPLICATION]: 'orange',
  [CARGO_STATUSES.IN_ROUTE]: 'blue',
  [CARGO_STATUSES.ON_WAREHOUSE]: 'purple',
  [CARGO_STATUSES.DELIVERED]: 'green'
};

// Cargo Types
export const CARGO_CHARACTERS = {
  NORMAL: 'обычный',
  OVERSIZED: 'негабаритный',
  DANGEROUS: 'опасный',
  FRAGILE: 'хрупкий'
} as const;

export const CARGO_CHARACTER_LABELS = {
  [CARGO_CHARACTERS.NORMAL]: 'Обычный',
  [CARGO_CHARACTERS.OVERSIZED]: 'Негабаритный',
  [CARGO_CHARACTERS.DANGEROUS]: 'Опасный',
  [CARGO_CHARACTERS.FRAGILE]: 'Хрупкий'
};

// Permissions
export const PERMISSIONS = {
  APPLICATIONS_VIEW: 'applications.view',
  APPLICATIONS_CREATE: 'applications.create',
  APPLICATIONS_EDIT: 'applications.edit',
  APPLICATIONS_DELETE: 'applications.delete',
  
  ROUTES_VIEW: 'routes.view',
  ROUTES_CREATE: 'routes.create',
  ROUTES_EDIT: 'routes.edit',
  ROUTES_DELETE: 'routes.delete',
  
  WAREHOUSES_VIEW: 'warehouses.view',
  WAREHOUSES_CREATE: 'warehouses.create',
  WAREHOUSES_EDIT: 'warehouses.edit',
  WAREHOUSES_DELETE: 'warehouses.delete',
  
  REFERENCES_VIEW: 'references.view',
  REFERENCES_CREATE: 'references.create',
  REFERENCES_EDIT: 'references.edit',
  REFERENCES_DELETE: 'references.delete',
  
  USERS_VIEW: 'users:view',
  USERS_CREATE: 'users:create',
  USERS_EDIT: 'users:edit',
  USERS_DELETE: 'users:delete',
  
  ADMIN_PANEL: 'admin:panel'
} as const; 